import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Copy, BookOpen, Zap, X } from 'lucide-react';
import { toast } from 'sonner';

const AutoFillBookmarklet = ({ company, onClose }) => {
  const [copied, setCopied] = useState(false);

  const generateBookmarklet = (company) => {
    const script = `
      (function() {
        // APT-AccessHub Auto-Fill for ${company.name}
        const username = '${company.username.replace(/'/g, "\\'")}';
        const password = '${company.password.replace(/'/g, "\\'")}';
        
        // Common selectors for username/email fields
        const usernameSelectors = [
          '#inputUsername',
          'input[type="email"]',
          'input[name*="email" i]',
          'input[name*="user" i]',
          'input[name*="login" i]',
          'input[id*="email" i]',
          'input[id*="user" i]',
          'input[id*="login" i]',
          'input[placeholder*="email" i]',
          'input[placeholder*="usuario" i]',
          'input[placeholder*="user" i]',
          'input[autocomplete="email"]',
          'input[autocomplete="username"]'
        ];
        
        // Common selectors for password fields
        const passwordSelectors = [
          '#inputPswd',
          'input[type="password"]',
          'input[name*="password" i]',
          'input[name*="pass" i]',
          'input[id*="password" i]',
          'input[id*="pass" i]',
          'input[autocomplete="current-password"]'
        ];
        
        // Function to wait for elements to be available
        const waitForElement = (selectors, timeout = 10000) => {
          return new Promise((resolve, reject) => {
            const startTime = Date.now();
            
            const checkElements = () => {
              for (const selector of selectors) {
                try {
                  const element = document.querySelector(selector);
                  if (element && element.offsetParent !== null && element.type !== 'hidden') {
                    resolve(element);
                    return;
                  }
                } catch (e) {
                  // Continue with next selector
                }
              }
              
              if (Date.now() - startTime > timeout) {
                reject(new Error('Elements not found after ' + timeout + 'ms'));
                return;
              }
              
              setTimeout(checkElements, 500);
            };
            
            checkElements();
          });
        };
        
        // Wait for elements and fill form
        (async () => {
          try {
            let usernameField = null;
            let passwordField = null;
            
            console.log('APT-AccessHub: Waiting for username field...');
            try {
              usernameField = await waitForElement(usernameSelectors, 8000);
              console.log('APT-AccessHub: Username field found');
            } catch (error) {
              console.log('APT-AccessHub: Username field not found, trying fallback...');
              const allInputs = document.querySelectorAll('input[type="text"], input[type="email"], input:not([type])');
              for (const input of allInputs) {
                if (input.offsetParent !== null && input.type !== 'hidden') {
                  usernameField = input;
                  break;
                }
              }
            }
            
            console.log('APT-AccessHub: Waiting for password field...');
            try {
              passwordField = await waitForElement(passwordSelectors, 8000);
              console.log('APT-AccessHub: Password field found');
            } catch (error) {
              console.log('APT-AccessHub: Password field not found, trying fallback...');
              const allPasswords = document.querySelectorAll('input[type="password"]');
              for (const input of allPasswords) {
                if (input.offsetParent !== null) {
                  passwordField = input;
                  break;
                }
              }
            }
            
            if (usernameField && passwordField) {
              console.log('APT-AccessHub: Both fields found, filling form...');
              
              // Wait a bit to ensure page is ready
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              // Clear and fill fields
              usernameField.value = '';
              passwordField.value = '';
              
              usernameField.focus();
              await new Promise(resolve => setTimeout(resolve, 200));
              usernameField.value = username;
              
              passwordField.focus();
              await new Promise(resolve => setTimeout(resolve, 200));
              passwordField.value = password;
              
              // Trigger all possible events
              const events = ['input', 'change', 'keyup', 'keydown', 'focus', 'blur'];
              events.forEach(eventType => {
                const event = new Event(eventType, { bubbles: true, cancelable: true });
                usernameField.dispatchEvent(event);
                passwordField.dispatchEvent(event);
              });
              
              // Visual feedback
              usernameField.style.backgroundColor = '#dcfce7';
              passwordField.style.backgroundColor = '#dcfce7';
              usernameField.style.border = '2px solid #22c55e';
              passwordField.style.border = '2px solid #22c55e';
              
              // Create success notification
              const notification = document.createElement('div');
              notification.innerHTML = '✅ Credenciales completadas para ${company.name}';
              notification.style.cssText = \`
                position: fixed;
                top: 20px;
                right: 20px;
                background: #22c55e;
                color: white;
                padding: 12px 20px;
                border-radius: 8px;
                font-family: -apple-system, BlinkMacSystemFont, sans-serif;
                font-size: 14px;
                font-weight: 600;
                z-index: 999999;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              \`;
              document.body.appendChild(notification);
              
              setTimeout(() => {
                if (notification.parentNode) {
                  notification.parentNode.removeChild(notification);
                }
              }, 4000);
              
              // Try to find submit button
              setTimeout(() => {
                const submitSelectors = [
                  'button[type="submit"]',
                  'input[type="submit"]',
                  'button:contains("Sign in")',
                  'button:contains("Login")',
                  'button:contains("Entrar")',
                  'button:contains("Acceder")',
                  '[role="button"][aria-label*="sign"]',
                  '.login-button',
                  '.submit-button'
                ];
                
                for (const selector of submitSelectors) {
                  const buttons = document.querySelectorAll(selector);
                  for (const button of buttons) {
                    const text = button.textContent?.toLowerCase() || '';
                    if (text.includes('sign') || text.includes('login') || text.includes('entrar') || 
                        text.includes('acceder') || button.type === 'submit') {
                      if (button.offsetParent !== null && !button.disabled) {
                        button.focus();
                        button.style.backgroundColor = '#fbbf24';
                        button.style.border = '2px solid #f59e0b';
                        break;
                      }
                    }
                  }
                }
              }, 500);
              
              console.log('APT-AccessHub: Form filled successfully!');
              
            } else {
              console.log('APT-AccessHub: Login fields not found');
              alert('❌ No se encontraron campos de login en esta página.\\n\\nAsegúrate de estar en la página de login de ${company.name}');
            }
            
          } catch (error) {
            console.error('APT-AccessHub error:', error);
            alert('❌ Error al completar campos: ' + error.message);
          }
        })();
      })();
    `;

    return `javascript:${script.replace(/\s+/g, ' ').trim()}`;
  };

  const handleCopyBookmarklet = async () => {
    const bookmarkletCode = generateBookmarklet(company);
    
    try {
      await navigator.clipboard.writeText(bookmarkletCode);
      setCopied(true);
      toast.success('¡Bookmarklet copiado!', {
        description: 'Pégalo en la barra de direcciones de la página de login',
        duration: 5000,
      });
      
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error('Error al copiar', {
        description: 'No se pudo copiar el código automático',
        duration: 3000,
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-orange-100 to-orange-200 p-2 rounded-lg">
              <Zap className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Auto-filler for {company.name}
              </h2>
              <p className="text-gray-500 text-sm">{company.login_url}</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-blue-900 flex items-center">
                <BookOpen className="h-5 w-5 mr-2" />
                How does it work?
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-blue-800">
              <p className="mb-3">
                For sites that block automatic auto-fill, you can use this 
                special "bookmarklet" that will fill the fields manually:
              </p>
              <ol className="list-decimal list-inside space-y-2">
                <li>Copy the auto-filler code (button below)</li>
                <li>Go to the {company.name} login page</li>
                <li>Paste the code in the address bar</li>
                <li>Press Enter - the fields will be filled automatically!</li>
              </ol>
            </CardContent>
          </Card>

          {/* Credentials Preview */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-gray-900">
                Credentials to be filled:
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Username / Email:</p>
                <p className="font-mono text-gray-900 bg-white px-3 py-2 rounded border">
                  {company.username}
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Password:</p>
                <p className="font-mono text-gray-900 bg-white px-3 py-2 rounded border">
                  {'•'.repeat(company.password.length)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Button */}
          <div className="text-center">
            <Button
              onClick={handleCopyBookmarklet}
              className={`btn-orange text-lg px-8 py-3 ${copied ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              {copied ? (
                <>
                  <Zap className="h-5 w-5 mr-2" />
                  ✅ Copied! Use it on {company.name}
                </>
              ) : (
                <>
                  <Copy className="h-5 w-5 mr-2" />
                  Copy Auto-filler
                </>
              )}
            </Button>
            
            <p className="text-xs text-gray-500 mt-3">
              El código es seguro y solo funciona en la página de {company.name}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              💡 Tip: Guarda este bookmarklet como favorito para uso futuro
            </p>
            <Button onClick={onClose} className="btn-primary">
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoFillBookmarklet;